/* Domovoi · Appearance: Light, Dark, or Auto (follows the phone).

   The redesign only (data-redesign on <html>, from the "redesign" switch).
   The choice is per device (domovoi.appearance); data-theme on <html> carries
   the result, which the redesign's tokens in colors_and_type.css follow. The
   inline <head> script in shell.html applies the same rule before first paint,
   so this module only keeps it current: a new choice, or the phone switching
   between light and dark while Auto. Screens not yet redrawn for the redesign
   keep data-theme="light" on their own container. */
import { storage } from './storage.js';
import { statusBarText } from './platform.js';

export const APPEARANCE_KEY = 'domovoi.appearance';
export const APPEARANCES = ['light', 'dark', 'auto'];
const BAR = { light: '#F4F0E5', dark: '#120A1E' };   // --bg in each theme, for the browser's own bar

export function getAppearance() {
  const v = storage.get(APPEARANCE_KEY);
  return APPEARANCES.includes(v) ? v : 'auto';
}

/** 'light' | 'dark' from a choice and whether the phone is dark. */
export function resolveTheme(pref, phoneDark) {
  return pref === 'dark' || (pref !== 'light' && pref !== 'dark' && !!phoneDark) ? 'dark' : 'light';
}

function phoneDark() {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch (e) { return false; }
}

export function currentTheme() { return resolveTheme(getAppearance(), phoneDark()); }

/** Put the current theme on <html>, the browser bar and the status bar. */
export function applyTheme(root) {
  root = root || document.documentElement;
  const t = currentTheme();
  root.setAttribute('data-theme', t);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', BAR[t]);
  statusBarText(t === 'dark' ? 'light' : 'dark');
  return t;
}

/** Take the theme off again (the switch turned off): today's look, light. */
export function clearTheme(root) {
  root = root || document.documentElement;
  root.removeAttribute('data-theme');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', BAR.light);
  statusBarText('dark');
}

export function setAppearance(v) {
  if (!APPEARANCES.includes(v)) return;
  storage.set(APPEARANCE_KEY, v);
  applyTheme();
  window.dispatchEvent(new CustomEvent('dv-theme', { detail: { appearance: v, theme: currentTheme() } }));
}

/** Follow the phone while the choice is Auto. Returns a function that stops. */
export function watchTheme() {
  let mq = null;
  try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch (e) { return () => {}; }
  const on = () => {
    if (getAppearance() !== 'auto' || !document.documentElement.hasAttribute('data-redesign')) return;
    applyTheme();
    window.dispatchEvent(new CustomEvent('dv-theme', { detail: { appearance: 'auto', theme: currentTheme() } }));
  };
  if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on);
  return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on); };
}
